import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Phone, MessageSquare } from "lucide-react";
import { formatPhoneFr, phoneDigitsFr } from "@/lib/formatters";

/**
 * Rend un numéro de téléphone cliquable qui, au clic, propose un menu
 * « 📞 Appeler » et « 💬 SMS ». Les liens `tel:` et `sms:` sont automatiquement
 * pris en charge par le smartphone (ou par l'app par défaut sur desktop).
 *
 * Props:
 *  - value: string — numéro brut (ou formaté)
 *  - className: string — classes supplémentaires sur le déclencheur
 *  - showIcon: bool — icône téléphone à gauche du numéro (défaut false)
 *  - "data-testid": string — data-testid appliqué au bouton déclencheur
 *
 * Si `value` est vide ou invalide (moins de 6 chiffres), on rend un fallback
 * texte statique non cliquable.
 */
export default function PhoneClickable({ value = "", className = "", showIcon = false, "data-testid": dataTestId }) {
  const [open, setOpen] = useState(false);
  const digits = phoneDigitsFr(value);
  const formatted = formatPhoneFr(value) || value || "";
  const isDialable = digits.length >= 6;
  if (!formatted) return null;
  if (!isDialable) {
    return <span className={className}>{formatted}</span>;
  }
  // Preserve the leading `+` from an international value in the tel: link.
  // If the caller passed "+33..." we want to keep the E.164 form.
  const cleanedDial = (String(value || "").trim().startsWith("+") ? "+" : "") + digits;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 text-[#0069B4] hover:text-[#662483] hover:underline transition-colors ${className}`}
          data-testid={dataTestId || "phone-clickable"}
          title="Cliquer pour appeler ou envoyer un SMS"
        >
          {showIcon && <Phone className="w-3.5 h-3.5" />}
          <span className="tabular-nums">{formatted}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2 rounded-none" data-testid="phone-clickable-menu">
        <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 px-1">{formatted}</div>
        <div className="grid grid-cols-2 gap-1">
          <a
            href={`tel:${cleanedDial}`}
            onClick={() => setOpen(false)}
            className="flex flex-col items-center gap-1 p-2 border border-green-200 bg-green-50 hover:bg-green-100 text-green-800 transition-colors"
            data-testid="phone-clickable-call"
          >
            <Phone className="w-4 h-4" />
            <span className="text-xs font-bold">Appeler</span>
          </a>
          <a
            href={`sms:${cleanedDial}`}
            onClick={() => setOpen(false)}
            className="flex flex-col items-center gap-1 p-2 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 transition-colors"
            data-testid="phone-clickable-sms"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-xs font-bold">SMS</span>
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
